import { Routes } from 'app/config/routes/consts';
import { Box, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { useMutation } from 'react-query';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { twoFactorSetup } from 'entities/Auth/api';
import { EAuthStep } from 'entities/Auth/api/types';
import { ColorPalette } from 'shared/consts/colorPalette';
import { isEmptyObject } from 'shared/helpers/isEmptyObject';
import { isEmptyObjectValues } from 'shared/helpers/isEmptyObjectValues';
import { BackButton } from 'shared/ui/BackButton';
import { Button } from 'shared/ui/Button';
import Clipboard from 'shared/ui/clipboard';
import { InputField } from 'shared/ui/InputField';
import { Paper } from 'shared/ui/Paper';
import { TwoFactorCode, TwoFactorCodeValidationSchema } from 'pages/auth/TwoFactor/twoFactorValidation';
import { getErrorMessage } from 'shared/helpers/getErrorMessage';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { useTwoFactorStore } from 'pages/auth/TwoFactor/twoFactorStore';

export const TwoFactorSetup: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { challenge, reset, completeSetup } = useTwoFactorStore();

	const goToSignIn = () => {
		reset();
		navigate(Routes.signIn, { replace: true });
	};

	const { mutate, isLoading } = useMutation({
		mutationKey: ['post/2fa-setup'],
		mutationFn: twoFactorSetup,
		onSuccess: (res) => {
			completeSetup(res.data.backupCodes);
			navigate(Routes.signIn, { replace: true });
		},
	});

	if (!challenge || challenge.step !== EAuthStep.TWO_FACTOR_SETUP) {
		return <Navigate to={Routes.signIn} replace />;
	}

	const handleSubmit = (values: TwoFactorCode, { setFieldError }: FormikHelpers<TwoFactorCode>) => {
		mutate(
			{ twoFactorToken: challenge.twoFactorToken, code: values.code.trim() },
			{
				onError: (err) => {
					if ((err as AxiosError).response?.status === 401) {
						showErrorNotification(err as AxiosError);
						goToSignIn();
						return;
					}
					setFieldError('code', getErrorMessage(err));
				},
			},
		);
	};

	return (
		<Paper title={t('auth.two_factor.setup_title') as string}>
			<BackButton action={goToSignIn} />
			<Typography variant={'body1'} color={ColorPalette.baliHai} sx={{ marginBottom: '24px' }}>
				{t('auth.two_factor.setup_description')}
			</Typography>

			{challenge.qrCode && (
				<Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
					<img src={challenge.qrCode} alt={'2FA QR code'} width={200} height={200} />
				</Box>
			)}

			{challenge.secret && (
				<Box sx={{ marginBottom: '24px' }}>
					<Typography variant={'body2'} color={ColorPalette.baliHai} sx={{ marginBottom: '4px' }}>
						{t('auth.two_factor.setup_secret_hint')}
					</Typography>
					<Clipboard
						text={challenge.secret}
						content={
							<Typography
								color={ColorPalette.astronaut}
								sx={{ fontFamily: 'monospace', fontWeight: 600, wordBreak: 'break-all' }}
							>
								{challenge.secret}
							</Typography>
						}
					/>
				</Box>
			)}

			<Formik
				initialValues={{ code: '' }}
				validationSchema={TwoFactorCodeValidationSchema}
				onSubmit={handleSubmit}
			>
				{({ errors, values }: FormikProps<TwoFactorCode>) => (
					<Form>
						<InputField
							name={'code'}
							label={t('auth.two_factor.code_label')}
							placeholder={t('auth.two_factor.code_placeholder')}
							autoComplete={'one-time-code'}
							inputProps={{ inputMode: 'numeric', maxLength: 6 }}
							autoFocus
							required
						/>
						<Button
							disabled={isLoading || !isEmptyObject(errors) || isEmptyObjectValues(values)}
							type={'submit'}
							fullWidth
							sx={{ marginTop: '12px' }}
						>
							{t('auth.two_factor.confirm')}
						</Button>
					</Form>
				)}
			</Formik>
		</Paper>
	);
};
